import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Linkedin } from "lucide-react";
import { useSettings } from "../lib/cms";

export const Footer = () => {
  const { settings } = useSettings();
  const s = settings || {};
  const companyName = s.company_name || "Maison Vellore";
  const phone = s.phone || "+31 20 123 45 67";
  const email = s.email || "contact@maisonvellore.nl";
  const address = s.address || "Herengracht 412, 1017 BZ Amsterdam";
  const insta = s.instagram_url;
  const linkedin = s.linkedin_url;
  const footerText =
    s.footer_text ||
    "Een particuliere handelsruimte voor sportwagens, youngtimers en uitzonderlijke automobielen.";

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-ink-950 border-t border-white/[0.06] mt-24 md:mt-32"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <div className="relative w-10 h-10 flex items-center justify-center border border-champagne/50">
                <span className="font-display text-champagne text-xl leading-none">V</span>
              </div>
              <div className="flex flex-col leading-none">
                <span className="font-display text-[26px] text-white tracking-tight">
                  {companyName}
                </span>
                <span className="text-[10px] tracking-[0.28em] uppercase text-zinc-500 mt-1">
                  {s.tagline || "Curated Automobilia"} · Anno 2011
                </span>
              </div>
            </div>
            <p className="mt-8 max-w-md text-sm text-zinc-400 font-body leading-relaxed">
              {footerText}
            </p>
          </div>

          <div className="md:col-span-3">
            <p className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body mb-6">
              Navigatie
            </p>
            <ul className="flex flex-col gap-3">
              {[
                { to: "/", label: "Home" },
                { to: "/aanbod", label: "Aanbod" },
                { to: "/services", label: "Services" },
                { to: "/over-ons", label: "Over ons" },
                { to: "/verhalen", label: "Verhalen" },
                { to: "/events", label: "Events" },
                { to: "/contact", label: "Contact" },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-sm text-zinc-300 hover:text-champagne transition-colors font-body"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body mb-6">
              Atelier
            </p>
            <ul className="flex flex-col gap-4">
              <li className="flex items-start gap-3 text-sm text-zinc-300 font-body">
                <MapPin size={14} className="mt-0.5 text-champagne-muted" />
                <span style={{ whiteSpace: "pre-line" }}>{address}</span>
              </li>
              <li>
                <a
                  href={`tel:${phone.replace(/\s/g, "")}`}
                  data-testid="footer-phone"
                  className="flex items-start gap-3 text-sm text-zinc-300 hover:text-champagne transition-colors font-body"
                >
                  <Phone size={14} className="mt-0.5 text-champagne-muted" />
                  {phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${email}`}
                  data-testid="footer-email"
                  className="flex items-start gap-3 text-sm text-zinc-300 hover:text-champagne transition-colors font-body"
                >
                  <Mail size={14} className="mt-0.5 text-champagne-muted" />
                  {email}
                </a>
              </li>
              {insta && (
                <li>
                  <a
                    href={insta}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-instagram"
                    className="flex items-start gap-3 text-sm text-zinc-300 hover:text-champagne transition-colors font-body"
                  >
                    <Instagram size={14} className="mt-0.5 text-champagne-muted" />
                    Instagram
                  </a>
                </li>
              )}
              {linkedin && (
                <li>
                  <a
                    href={linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-linkedin"
                    className="flex items-start gap-3 text-sm text-zinc-300 hover:text-champagne transition-colors font-body"
                  >
                    <Linkedin size={14} className="mt-0.5 text-champagne-muted" />
                    LinkedIn
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/[0.06] flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-body">
            © {new Date().getFullYear()} {companyName} B.V.
          </p>
          <div className="flex items-center gap-6">
            <span className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-body">
              Algemene voorwaarden
            </span>
            <span className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-body">
              Privacy
            </span>
            <Link
              to="/admin"
              className="text-[11px] tracking-[0.2em] uppercase text-zinc-600 hover:text-champagne transition-colors font-body"
              data-testid="footer-admin-link"
            >
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
